import {generateTokensFromObject} from '../__utils';
import {coreColorTokens, coreColors} from '../colors';

import {darkDarkerTheme} from './darkDarkerTheme';

import type {ThemeColors} from '../colors';
import type {ThemeObject} from '../theme.types';

/**
 * These values are inlined into <body> styles via jade to prevent flash of
 * unstyled content (FOUC) before the theme stylesheet loads.
 *
 * - `background` — body background. Keep in sync with the `background1` token
 *   in the colors object below.
 * - `loadingTrack` — preloader progress-bar track. Keep in sync with the
 *   `background2` token in the colors object below.
 * - `loadingBackground` — preloader screen background. White in light theme,
 *   black in dark themes. Not tied to a colors-object token.
 * - `loadingBloomOpacity` — opacity for the preloader's glow stack. Subtle on
 *   light backgrounds, full on dark backgrounds.
 * - `loadingText` — preloader text color. Mirrors the `textPrimary`
 *   token so loader copy stays readable on the loader background.
 */
export const DARK_BRIGHTER_HTML_BACKGROUND_COLORS = {
  background: coreColors.d2.gray[1000],
  loadingTrack: coreColors.d2.gray[800],
  loadingBackground: coreColors.d2.black,
  loadingBloomOpacity: 1,
  loadingText: coreColors.d2.white,
};

const colors: ThemeColors = {
  ...darkDarkerTheme.colors,
  actionSecondaryText: '#ededed',
  actionSecondaryTextHover: '#ededed',
  /**
   * Simplifying background colors.
   * The previous background 1 ... 5 need to be consolidated (renaming callsites)
   * to the primary ... tertiary values.
   *
   * @see https://webflow.atlassian.net/browse/DS-761
   */
  backgroundPrimary: coreColorTokens.d2.gray[1000],
  backgroundSecondary: coreColorTokens.d2.gray[800],
  backgroundTertiary: '#4c4c4c',
  background1: coreColorTokens.d2.gray[1000],
  background2: coreColorTokens.d2.gray[800],
  background3: '#4c4c4c',
  grayIcon: '#b8b8b8',
  uiBorderSolid: '#555555',
  uiCanvasBackground: '#4c4c4c',
  uiDragOverlayBackground: '#4c4c4c',
  uiInputBackground: 'rgba(0, 0, 0, 0.25)',
  uiMenuContentBackground: '#4c4c4c',
  uiMenuContentBackgroundLight: coreColorTokens.d2.gray[800], // $background2
  uiPopoverBackdrop: coreColorTokens.d2.blackA40,
};

export const darkBrighterTheme: ThemeObject = {
  ...darkDarkerTheme,
  colors,
};

export const darkBrighterCSSVariables =
  generateTokensFromObject(darkBrighterTheme).setTokens;
